import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage } from '@monksflow/monks-ui';
import { getInitials } from '@/shared/utils/getInitials';
// Type-only import keeps the entities→entities FSD rule satisfied.
import type { MentionOption } from '@/entities/project-comment';
import { projectMembersQuery } from '../api/project-member.api';

/**
 * Project members as @-mention options (people only — agents are appended by
 * brief-side callers). Avatar nodes are built here so the generic MentionList
 * stays free of entity imports.
 */
export function usePeopleMentionOptions(
  workspaceId: string | null | undefined,
  platformProjectId: string | null | undefined,
): MentionOption[] {
  const { data } = useQuery(projectMembersQuery(workspaceId, platformProjectId));

  return useMemo(
    () =>
      (data?.data ?? []).map((member) => ({
        id: member.id,
        label: member.name,
        type: 'user' as const,
        avatarSlot: (
          <Avatar className="size-5 shrink-0">
            {member.avatarUrl && <AvatarImage src={member.avatarUrl} alt={member.name} />}
            <AvatarFallback className="text-[10px]">{getInitials(member.name)}</AvatarFallback>
          </Avatar>
        ),
      })),
    [data],
  );
}
